import { z } from 'zod';

import { localeSchema, type Locale } from './locales.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * YASAL METİNLER — footer'ın "Yasal" sütununun sözleşmesi
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Yedi belge: kullanım koşulları, ön bilgilendirme formu, mesafeli satış
 * sözleşmesi, teslimat ve iade şartları, gizlilik politikası, KVKK/GDPR
 * aydınlatma metni, çerez politikası. Metinler `legal_documents` +
 * `legal_document_translations` tablolarında; bu dosya arayüzün gördüğü BİÇİMİ
 * tanımlıyor.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Metinlerin KENDİSİ burada değil
 * ---------------------------------------------------------------------------
 * `help-center.ts`ten farklı olarak yasal metin kodda durmuyor: onu değiştiren
 * şey ürün değil hukuk ve değiştiren kişi çoğu zaman kod yazmıyor. Yönetimi
 * `/backstage/legal` ekranından yapılıyor.
 *
 * Başlangıç içeriği bir TOHUM (`@mentorivo/shared/legal-seed`) ve tek
 * tüketicisi `packages/db/seed/legal.ts`.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Belgeler
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Slug'lar adresin kendisi — `/privacy`, `/terms` …
 *
 * 🔴 `apps/web/src/lib/site-links.ts` ile birebir aynı olmak ZORUNDA: footer
 * oradan okuyor, sayfa dosyaları buradan. İki liste ayrıştığı gün footer
 * var olmayan bir adrese bağlanır.
 *
 * Dizideki sıra EKRANDAKİ sıra: okuma sırası alfabetik değil mantıksal —
 * önce hizmetin sözleşmesi (koşullar), sonra satın almanın sözleşmesi (ön
 * bilgilendirme ve mesafeli satış — bu ikisi bu sırayla okunur, form satıştan
 * ÖNCE verilen bilgidir) ve satın almanın sonrası (teslimat ve iade), sonra
 * verinin ne olduğu (gizlilik), hukuki dayanağı (KVKK/GDPR) ve en sonda
 * teknik ayrıntısı (çerezler).
 *
 * `delivery-returns` 14 Eki 2026'da eklendi: iyzico üye iş yeri incelemesi
 * "Teslimat ve İade Şartları"nı ayrı bir sayfa olarak istiyor. Yeni bir
 * taahhüt getirmiyor — iki satış belgesinin ifa/iptal/iade hükümlerini tek
 * sayfada topluyor; çelişkide Mesafeli Satış Sözleşmesi esas. Onay kutusuna
 * bu yüzden BAĞLI DEĞİL (`SALES_LEGAL_SLUGS` iki belge kaldı).
 *
 * 🔴 Satış belgeleri ödeme ekranındaki onay kutusuna bağlı: slug değişirse
 * `checkout-panel.tsx`teki bağlantılar ve `PayService.start`ın denetim
 * kaydına yazdığı sürüm bilgisi de değişmeli.
 */
export const LEGAL_DOC_SLUGS = [
  'terms',
  'pre-information',
  'distance-sales',
  'delivery-returns',
  'privacy',
  'data-protection',
  'cookies',
] as const;

/**
 * Ödeme ekranındaki onay kutusunun bağladığı belgeler — okuma sırasıyla.
 *
 * Üç yerde birden okunuyor ve üçü de bu listeden besleniyor: ödeme ekranı
 * bağlantıları (`checkout-panel.tsx`), onayın denetim kaydına yazılan sürüm
 * bilgisi (`PayService.start`) ve panelin "yasal metinler" satırı. Elle
 * yazılan dördüncü bir kopya, sözleşme eklendiği gün eksik kalırdı.
 */
export const SALES_LEGAL_SLUGS = [
  'pre-information',
  'distance-sales',
] as const satisfies readonly LegalDocSlugCandidate[];

/// `LEGAL_DOC_SLUGS`in içinden seçildiğini derleyiciye söyleyen yardımcı tip.
type LegalDocSlugCandidate = (typeof LEGAL_DOC_SLUGS)[number];

export const legalDocSlugSchema = z.enum(LEGAL_DOC_SLUGS);
export type LegalDocSlug = (typeof LEGAL_DOC_SLUGS)[number];

export const LEGAL_DOC_STATUSES = ['DRAFT', 'PUBLISHED'] as const;
export const legalDocStatusSchema = z.enum(LEGAL_DOC_STATUSES);
export type LegalDocStatus = (typeof LEGAL_DOC_STATUSES)[number];

// ═══════════════════════════════════════════════════════════════════════════
// Gövde blokları
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Yasal metnin blok sözlüğü — blog'unkinin (`blogBlockSchema`) akrabası ama
 * aynısı değil. İki yönde de fark var ve ikisi de bilinçli.
 *
 * | Blog'da var, burada YOK | Neden |
 * |---|---|
 * | `image`, `gallery` | Yasal metin görselle anlatılmaz. Bir sözleşmeye ekran görüntüsü koymak, metnin dışında bağlayıcılığı belirsiz bir unsur eklemek demek. |
 * | `quote` | Alıntı BAŞKASININ sözü. Yasal metin kendi adına konuşur; mevzuat atfı paragrafın içinde yapılır ("KVKK m.5/2-c"). |
 * | `divider` | Bölümleri `heading` ayırıyor. İkinci bir ayırma dili, numaralı bir belgede ritmi bozar. |
 *
 * | Burada var, blog'da YOK | Neden |
 * |---|---|
 * | `table` | KVKK aydınlatma metni "veri kategorisi × amaç × hukuki sebep" tablosu istiyor, çerez politikası da çerez envanteri. İkisi de düz paragrafa sığmıyor — sığdırılırsa okunmaz olur ve tam da okunması gereken kısım budur. Blog'da bilerek yoktu (`blog.ts`: "gerekirse eklenecek ilk tip o"); yasal metin o gerekçeyi getirdi. |
 *
 * `note` ikisinde de var ve burada işi farklı: blogda "şunu atlamayın",
 * burada "bu metin bilgilendirme amaçlıdır" gibi metnin KENDİ statüsüne dair
 * uyarılar.
 */

/** Gövdedeki ana başlık — içindekiler listesi (TOC) yalnız bunlardan kuruluyor. */
export const legalHeadingBlockSchema = z.object({
  type: z.literal('heading'),
  text: z.string().min(1).max(200),
  /**
   * Bağlantı çıpası (`#veri-sahibi-haklari`).
   *
   * Tohumda YAZILMIYOR: metinden türetiliyor ve türetme sunucuda tek yerde
   * (`legal.mappers.ts` → `withAnchors`, blog ile ORTAK `anchorFor`).
   *
   * 🔴 Çıpanın kalıcılığı burada bir yasal mesele: "gizlilik politikasının
   * 7. maddesi" diye verilen bir bağlantı bir başlık düzeltmesiyle kırılırsa
   * atıf kaybolur. Bu yüzden `anchor` gövdede SAKLANABİLİR — editör bir kez
   * yazıldıktan sonra türetmeyi tekrar ETMİYOR (`withAnchors` mevcut çıpayı
   * koruyor).
   */
  anchor: z.string().max(120).optional(),
});

/**
 * Tablo.
 *
 * Başlık satırı ZORUNLU (`headers`): başlıksız bir tablo, ekran okuyucuda
 * hücrelerin neyi anlattığını söyleyemez ve yasal bir tabloda sütunun ne
 * olduğu (amaç mı, hukuki sebep mi) bilginin kendisi.
 *
 * Satırlar `string[][]` ve sütun sayısının başlıkla EŞLEŞMESİ doğrulanıyor —
 * eksik hücre, tabloyu ekranda kaydırır ve hangi verinin hangi amaca ait
 * olduğunu bozar.
 */
export const legalTableBlockSchema = z
  .object({
    type: z.literal('table'),
    /// Tablonun ne anlattığı. `<caption>` olarak basılıyor; ekran okuyucu
    /// tabloya girmeden önce bunu okuyor.
    caption: z.string().max(200).optional(),
    headers: z.array(z.string().min(1).max(120)).min(2).max(5),
    rows: z
      .array(z.array(z.string().max(600)).min(2).max(5))
      .min(1)
      .max(60),
  })
  .refine((table) => table.rows.every((row) => row.length === table.headers.length), {
    message: 'Tablo satırlarındaki hücre sayısı başlık sayısıyla aynı olmalı.',
    path: ['rows'],
  });

export const legalBlockSchema = z.discriminatedUnion('type', [
  legalHeadingBlockSchema,
  z.object({ type: z.literal('subheading'), text: z.string().min(1).max(200) }),
  z.object({ type: z.literal('paragraph'), text: z.string().min(1).max(4000) }),
  z.object({
    type: z.literal('list'),
    /// `true` = numaralı. Yasal metinde numaralı liste bir ATIF yüzeyi
    /// ("madde 4, bent b"), madde imli liste yalnızca sayma.
    ordered: z.boolean().default(false),
    items: z.array(z.string().min(1).max(1000)).min(1).max(40),
  }),
  z.object({
    type: z.literal('note'),
    title: z.string().max(120).optional(),
    text: z.string().min(1).max(1500),
  }),
  legalTableBlockSchema,
]);

export type LegalBlock = z.infer<typeof legalBlockSchema>;
export type LegalHeadingBlock = z.infer<typeof legalHeadingBlockSchema>;
export type LegalTableBlock = z.infer<typeof legalTableBlockSchema>;

/**
 * Gövdenin tamamı.
 *
 * Üst sınır blogunkinin (200) iki katı: bir kullanım koşulları metni 20-25
 * bölüm, her bölüm 3-6 blok — 200'e sığmıyor.
 *
 * `Json` kolonundan OKURKEN de çalışıyor. Tanınmayan blok DÜŞÜRÜLÜYOR ve
 * düşürüldüğü loglanıyor (`legal.service.ts`); sayfayı 500'e düşürmek,
 * yasal metni tamamen erişilemez kılmak olurdu.
 */
export const legalBodySchema = z.array(legalBlockSchema).max(400);

// ═══════════════════════════════════════════════════════════════════════════
// Doldurulmamış yer tutucu
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Şirketin hukuki kimliğine dair değerler metinde `[[ÇİFT KÖŞELİ PARANTEZ]]`
 * içinde duruyor: unvan, adres, MERSİS, vergi dairesi, VERBİS, KEP.
 *
 * Uydurulmuş bir MERSİS numarası, yokluğundan kötü olurdu — okuyan kişi onu
 * gerçek sanar. Bu yüzden değerler boş bırakılmadı, GÖZE BATACAK biçimde
 * işaretlendi. Biçim ayrıca makineyle bulunabilir olsun diye seçildi: çift
 * köşeli parantez normal hukuk metninde geçmiyor, tek köşeli geçiyor
 * ("[…] hükümleri saklıdır" gibi).
 *
 * 🔴 `g` bayrağı YOK ve olmamalı: `g` taşıyan bir `RegExp` `lastIndex`
 * durumu tutuyor ve ardışık `test()` çağrılarında dönüşümlü olarak `false`
 * dönüyor — yani bloğun ikisinden biri "temiz" görünürdü.
 */
export const LEGAL_PLACEHOLDER_PATTERN = /\[\[[^\]\n]{2,80}\]\]/;

/**
 * Bloklarda doldurulmamış yer tutucu var mı?
 *
 * İki yerden çağrılıyor ve ikisi de gerekli:
 *   • API, panelin liste satırına `hasPlaceholders` yazarken — yayındaki
 *     eksik bir belge listede uyarıyla görünsün diye.
 *   • Panel editörü, yazarken — kaydeden kişi metinde ne kaldığını görsün.
 *
 * Bu yüzden sözleşme dosyasında: tohum dosyasında dursaydı panel onu import
 * etmek için ~70 KB metni de tarayıcıya indirirdi.
 */
export function hasLegalPlaceholder(blocks: LegalBlock[]): boolean {
  for (const block of blocks) {
    switch (block.type) {
      case 'heading':
      case 'subheading':
      case 'paragraph':
        if (LEGAL_PLACEHOLDER_PATTERN.test(block.text)) return true;
        break;
      case 'note':
        if (LEGAL_PLACEHOLDER_PATTERN.test(block.text)) return true;
        if (block.title && LEGAL_PLACEHOLDER_PATTERN.test(block.title)) return true;
        break;
      case 'list':
        if (block.items.some((item) => LEGAL_PLACEHOLDER_PATTERN.test(item))) return true;
        break;
      case 'table':
        if (block.caption && LEGAL_PLACEHOLDER_PATTERN.test(block.caption)) return true;
        if (block.headers.some((cell) => LEGAL_PLACEHOLDER_PATTERN.test(cell))) return true;
        if (block.rows.some((row) => row.some((cell) => LEGAL_PLACEHOLDER_PATTERN.test(cell))))
          return true;
        break;
    }
  }
  return false;
}

// ═══════════════════════════════════════════════════════════════════════════
// Sorgular
// ═══════════════════════════════════════════════════════════════════════════

export const legalDetailQuerySchema = z.object({
  /// Verilmezse İngilizce (varsayılan dil). Arayüz her zaman gönderiyor.
  locale: localeSchema.optional(),
});
export type LegalDetailQuery = z.infer<typeof legalDetailQuerySchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Yanıtlar — herkese açık
// ═══════════════════════════════════════════════════════════════════════════

export interface LegalTocEntry {
  anchor: string;
  text: string;
}

/**
 * Belgeler arası geçiş şeridinin bir kalemi.
 *
 * Her yasal sayfanın başında dördü birden duruyor: bu metinler birbirine atıf
 * yapıyor ("çerez ayrıntısı için Çerez Politikası") ve okuyucuyu footer'a geri
 * göndermek, aradığı cümleyi bulmasını zorlaştırır.
 */
export interface LegalDocLink {
  slug: string;
  title: string;
  /// Şeritte etkin kalemi işaretlemek için — arayüz kendi adresiyle
  /// karşılaştırmasın diye sunucu söylüyor.
  current: boolean;
}

export interface LegalDocDetail {
  slug: string;
  title: string;
  lead: string;
  version: string;
  /// ISO 8601. `null` OLAMAZ — yalnız yayınlanmış belge dönüyor ve
  /// yayınlanmışın yürürlük tarihi var (kontrol `legal.service.ts`).
  effectiveAt: string;
  /// Son metin düzeltmesi. Yürürlük tarihinden SONRAYSA arayüz ayrıca basıyor:
  /// "yürürlük 1 Eylül, son güncelleme 14 Eylül" bilgi taşıyor, aynı gün ise
  /// aynı şeyi iki kez söylemek olurdu.
  updatedAt: string;
  body: LegalBlock[];
  toc: LegalTocEntry[];
  seoTitle: string | null;
  seoDescription: string | null;
  locale: Locale;
  /**
   * İstenen dilde çeviri YOKTU, bu metin başka dilden geliyor. Değer İSTENEN
   * (eksik) dil; gösterilen metnin dili `locale`.
   *
   * Zincir: istenen dil → İngilizce → Türkçe → eldeki ilk dil
   * (`common/content-locale.ts`). Son basamak yalnız burada: footer'daki
   * yasal bağlantı "yakında" olamaz. Sayfa bu bayrağı görünce metnin üstüne
   * bir not basıyor; sessizce başka dilde bir sözleşme göstermek,
   * gösterilmemesinden kötü olurdu.
   *
   * 29 Eyl 2026'dan beri blog ve değişiklik kaydı da çevirisi üretilen on bir
   * dilde aynı sözleşmeyle düşüyor; `en`/`tr`de eksik çeviriyi eskisi gibi
   * gizliyorlar.
   */
  fallbackLocale: Locale | null;
  /// Şeritteki dört bağlantı. Sunucudan geliyor çünkü hangi belgelerin
  /// YAYINDA olduğunu yalnız sunucu biliyor.
  siblings: LegalDocLink[];
}

export interface LegalIndexEntry {
  slug: string;
  title: string;
  lead: string;
  version: string;
  effectiveAt: string;
  updatedAt: string;
  locale: Locale;
  fallbackLocale: Locale | null;
}

export interface LegalIndexResponse {
  documents: LegalIndexEntry[];
  locale: Locale;
}

export const LegalErrorCode = {
  DOC_NOT_FOUND: 'LEGAL_DOC_NOT_FOUND',
  /// Yayına alınmak istenen belgenin yürürlük tarihi ya da o dilde metni yok.
  NOT_PUBLISHABLE: 'LEGAL_DOC_NOT_PUBLISHABLE',
  /// Varsayılan dilin (İngilizce) çevirisi silinemez — düşülecek bir dil
  /// kalmazdı.
  DEFAULT_LOCALE_REQUIRED: 'LEGAL_DEFAULT_LOCALE_REQUIRED',
} as const;
export type LegalErrorCode = (typeof LegalErrorCode)[keyof typeof LegalErrorCode];

// ═══════════════════════════════════════════════════════════════════════════
// Yönetim paneli — /backstage/legal
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Panelin liste satırı.
 *
 * Çeviri METNİ taşımıyor, yalnız hangi dillerin dolu olduğunu: dört belgenin
 * iki dildeki tam metni tek bir liste isteğinde yüz kilobaytlarca. Metin,
 * belge açıldığında geliyor (bildirim şablonları ekranıyla aynı karar).
 */
export interface AdminLegalDocRow {
  slug: string;
  status: LegalDocStatus;
  version: string;
  order: number;
  effectiveAt: string | null;
  updatedAt: string;
  /// Hangi dillerde metin var. Sıra `LOCALES`in kendi sırası (`locales.ts`,
  /// on üç dil) — panelde sekmelerin sırası da bu.
  locales: { locale: Locale; title: string; updatedAt: string }[];
  /**
   * Herhangi bir dildeki metinde doldurulmamış `[[…]]` var mı?
   *
   * SUNUCUDA hesaplanıyor: liste satırı gövdeyi taşımıyor (taşısaydı yanıt
   * yüz kilobaytlara çıkardı), dolayısıyla panel bunu kendisi ölçemez.
   * Yayındaki bir belgede `true` olması, listede uyarı demek.
   */
  hasPlaceholders: boolean;
}

export interface AdminLegalTranslation {
  locale: Locale;
  title: string;
  lead: string;
  body: LegalBlock[];
  seoTitle: string | null;
  seoDescription: string | null;
  updatedAt: string;
  /**
   * Okurken düşürülen blok sayısı.
   *
   * Editör bunu göstermek ZORUNDA: kolon şema baskısı olmayan `Json` ve elle
   * düzenlenebiliyor. Sessizce düşürülen bir blok, "kaydettim ama bir madde
   * kayboldu" demek — yasal metinde bunun sessiz kalması kabul edilemez.
   */
  droppedBlocks: number;
}

export interface AdminLegalDocDetail {
  slug: string;
  status: LegalDocStatus;
  version: string;
  order: number;
  effectiveAt: string | null;
  createdAt: string;
  updatedAt: string;
  translations: AdminLegalTranslation[];
}

/**
 * Belgenin dil ÜSTÜ alanları.
 *
 * `status` burada: yayına alma iki dili birden ilgilendiriyor. Çevirinin
 * kendi yayın durumu YOK — "Türkçesi yayında, İngilizcesi taslak" bir
 * sözleşme için anlamsız; ikisi aynı belgenin iki dili.
 */
export const adminLegalMetaSchema = z.object({
  status: legalDocStatusSchema,
  version: z.string().min(1).max(40),
  /// ISO 8601 gün ya da tam zaman. `null` yalnız taslakta geçerli — kontrol
  /// serviste, çünkü `status` ile ilişkili bir kural ve zod tek alana bakıyor.
  effectiveAt: z.iso.datetime().nullable(),
  order: z.coerce.number().int().min(0).max(99),
});
export type AdminLegalMetaInput = z.infer<typeof adminLegalMetaSchema>;

/**
 * Bir dildeki metnin tamamı.
 *
 * Kısmi güncelleme (PATCH) YOK ve bu bilinçli: yasal metin bir bütün olarak
 * gözden geçirilip bir bütün olarak kaydedilir. Alan alan yamalamak, iki
 * paragrafı birbiriyle çelişen bir sözleşme üretmenin en kolay yolu.
 */
export const adminLegalTranslationSchema = z.object({
  title: z.string().min(1).max(200),
  lead: z.string().min(1).max(600),
  body: legalBodySchema.min(1),
  seoTitle: z.string().max(200).nullable().default(null),
  seoDescription: z.string().max(400).nullable().default(null),
});
export type AdminLegalTranslationInput = z.infer<typeof adminLegalTranslationSchema>;
